<template>
  <div id="app">
    <BlogNav v-if="$route.path.startsWith('/blog')" />
    <nav v-if="isNavbarVisible" class="my-nav">
      <a class="my-nav-brand" href="#">
        <img src="@/assets/logomark-white.png" :alt="$t('navbar.logoAlt')" class="my-nav-logo">
      </a>
      <ul class="my-nav-links">
        <li v-if="$route.name !== 'contact'">
          <router-link class="my-nav-link" :to="{ name: 'dashboard' }" active-class="active">
            {{ $t('navbar.myResearch') }}
          </router-link>
        </li>
        <li v-if="$route.name !== 'contact'">
          <router-link class="my-nav-link" :to="{ name: 'contact' }">
            {{ $t('navbar.contactUs') }}
          </router-link>
        </li>

        <!-- ✅ LIENS ADMIN DYNAMIQUES -->
        <li v-for="menuItem in adminMenuItems" :key="menuItem.name">
          <router-link
            class="my-nav-link"
            :to="{ name: menuItem.route_name }"
          >
            {{ menuItem.label }}
          </router-link>
        </li>

        <li>
          <a class="my-nav-link disabled" href="#"></a>
        </li>
      </ul>
      <div class="my-nav-settings">
        <div class="settings-dropdown">
          <a class="my-nav-link" href="#" @click.prevent="toggleSettingsMenu">
            <img src="@/assets/settings.svg" :alt="$t('navbar.settingsAlt')" class="my-nav-icon">
          </a>
          <transition name="fade-down">
            <div class="settings-menu" v-if="showSettingsMenu">
              <!-- User info header -->
              <div class="user-profile">
                <div class="user-avatar">
                  <span>{{ userInitials }}</span>
                </div>
                <div class="user-info">
                  <p class="user-email">{{ userEmail }}</p>
                </div>
              </div>

              <!-- Subscription section -->
              <div class="subscription-box" v-if="!loadingSub">
                <!-- Cas 1: Utilisateur avec abonnement individuel actif ET sans cabinet -->
                <div v-if="hasIndividualSubscription && !cabinetId" class="subscription-status">
                  <div class="plan-header">
                    <h3 class="plan-title">{{ $t('navbar.subscription') }}</h3>
                    <span class="plan-badge" :class="'plan-' + sub.plan">{{ planLabel }}</span>
                  </div>

                  <div class="plan-actions" style="margin: 15px;">
                    <button class="billing-btn" @click="openBillingPortal">
                      {{ $t('navbar.manageSubscription') }}
                    </button>
                  </div>
                </div>

                <!-- Cas 2: Utilisateur SANS accès du tout (ne peut pas faire de requêtes ET pas de cabinet) -->
                <div v-else-if="!hasAccessToRequests" class="subscription-status no-plan">
                  <div class="plan-header">
                    <h3 class="plan-title">{{ $t('navbar.subscription') }}</h3>
                    <span class="plan-badge plan-none">{{ $t('navbar.noPlan') }}</span>
                  </div>

                  <p v-if="sub.status === 'past_due'" class="payment-alert">
                    {{ $t('navbar.paymentFailedAlert') }}
                  </p>

                  <div class="plan-actions">
                    <button @click="showSubscriptionModal = true" class="upgrade-btn">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16"/>
                        <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4"/>
                      </svg>
                      {{ $t('navbar.upgrade') }}
                    </button>
                  </div>
                </div>

                <!-- Cas 3: Utilisateur avec accès via cabinet (admin ou membre) = RIEN -->
                <!-- Pas de section d'abonnement affichée -->
              </div>

              <!-- Loading placeholder -->
              <div v-else class="loading-subscription">
                <div class="loading-spinner"></div>
                <span>{{ $t('navbar.loadingInfo') }}</span>
              </div>

              <!-- Account actions section -->
              <div class="account-actions">
                <router-link to="/change-password" class="change-password-link" @click.native="showSettingsMenu = false">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M8 1a2 2 0 0 1 2 2v4H6V3a2 2 0 0 1 2-2zm3 6V3a3 3 0 0 0-6 0v4a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2zM5 8h6a1 1 0 0 1 1 1v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z"/>
                  </svg>
                  {{ $t('navbar.changePassword') }}
                </router-link>
                <button class="signout-button" @click="handleSignout">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                    <path fill-rule="evenodd" d="M10 12.5a.5.5 0 0 1-.5.5h-8a.5.5 0 0 1-.5-.5v-9a.5.5 0 0 1 .5-.5h8a.5.5 0 0 1 .5.5v2a.5.5 0 0 0 1 0v-2A1.5 1.5 0 0 0 9.5 2h-8A1.5 1.5 0 0 0 0 3.5v9A1.5 1.5 0 0 0 1.5 14h8a1.5 1.5 0 0 0 1.5-1.5v-2a.5.5 0 0 0-1 0z"/>
                    <path fill-rule="evenodd" d="M15.854 8.354a.5.5 0 0 0 0-.708l-3-3a.5.5 0 0 0-.708.708L14.293 7.5H5.5a.5.5 0 0 0 0 1h8.793l-2.147 2.146a.5.5 0 0 0 .708.708z"/>
                  </svg>
                  {{ $t('navbar.signout') }}
                </button>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </nav>

    <!-- Modal de souscription -->
    <SubscriptionModal
      :show="showSubscriptionModal"
      :payment-failed="sub.status === 'past_due'"
      @close="showSubscriptionModal = false"
      @select-plan="upgradeThroughModal"
      @navigate-to-contact="navigateToContact"
      @navigate-to-trial="navigateToTrial"
      @navigate-to-student="navigateToStudent"
    />

    <router-view></router-view>
  </div>
</template>

<script>
import { logout } from "@/supabase/auth";
import { supabase } from "@/supabase/supabaseClient";
import { getSubscriptionCached, getSupabaseSession, clearSessionCache } from "@/services/api";
import SubscriptionModal from './components/SubscriptionModal.vue';
import { startHeartbeat, stopHeartbeat } from '@/services/heartbeat';
import { getOwnProfile } from '@/supabase/profile';

import BlogNav from '@/components/BlogNav.vue'

const API_URL = process.env.VUE_APP_API_URL || 'https://api.query-juriste.com'

export default {
  name: "App",
  components: {
    SubscriptionModal,
    BlogNav
  },
  data() {
    return {
      showSettingsMenu: false,
      showSubscriptionModal: false,
      loadingSub: true,
      sub: { plan: "none", status: "none", queries_used: 0 },
      userEmail: "",
      authSubscription: null,
      profileLoaded: false,
      isCabinetAdmin: false,
      cabinetId: null,
      userId: null,

      // ✅ NOUVEAU : Liste des liens admin dynamiques
      adminMenuItems: [],
      menuItemsLoaded: false,
      isRefreshingSubscription: false,
      isLoadingAdminMenu: false,
      hasLoadedOnce: false, // Évite de bloquer l'UI après le premier chargement
    };
  },

  computed: {
    isNavbarVisible() {
      return ["dashboard", "contact", "example", "student", "trial",
              "ConditionsVente", "ConditionsUtilisation", "adminConsole",
              "adminDashboard", "activityPage", "trialMonitoring", "feedbackMonitoring", "DossierPage", "AgentPage"]
              .includes(this.$route.name);
    },
    planLabel() {
      switch (this.sub.plan) {
        case "student":
          return this.$t('navbar.plans.student');
        case "premium":
          return this.$t('navbar.plans.premium');
        case "trialing":
          return this.$t('navbar.plans.trialing');
        case "lifetime_free":
          return this.$t('navbar.plans.lifetimeFree');
        default:
          return this.$t('navbar.plans.none');
      }
    },

    expiredPlanLabel() {
      if (this.sub.status === 'past_due') return this.$t('navbar.plans.paymentFailed');
      if (this.sub.plan === 'student') return this.$t('navbar.plans.studentExpired');
      if (this.sub.plan === 'premium') return this.$t('navbar.plans.premiumExpired');
      return this.$t('navbar.plans.expired');
    },

    showBigBossConsole() {
      return this.adminBigBossIds.includes(this.userId);
    },

    // Calcul de canRequest (accès individuel uniquement, sans cabinet)
    canRequestViaIndividualPlan() {
      if (this.sub.plan === "lifetime_free") return true;
      const active = this.sub.status === "active" || this.sub.status === "trialing";
      const paying = ["student", "premium", "trialing"].includes(this.sub.plan);

      let periodValid = true;
      if (this.sub.current_period_end) {
        const periodEnd = new Date(this.sub.current_period_end);
        const now = new Date();
        periodValid = periodEnd > now;
      }

      const underQueryLimit = this.sub.queries_used < 100000;
      const hasQuota = this.quotaRemaining > 0;

      return active && paying && periodValid && hasQuota && underQueryLimit;
    },

    // Vérifie si l'utilisateur a un abonnement individuel actif (student ou premium)
    hasIndividualSubscription() {
      const isIndividualPlan = ["student", "premium"].includes(this.sub.plan);
      const isActive = this.sub.status === "active";

      let periodValid = true;
      if (this.sub.current_period_end) {
        const periodEnd = new Date(this.sub.current_period_end);
        const now = new Date();
        periodValid = periodEnd > now;
      }

      return isIndividualPlan && isActive && periodValid;
    },

    // Vérifie si l'utilisateur a accès aux requêtes (via abonnement OU cabinet)
    hasAccessToRequests() {
      // Accès via abonnement individuel
      if (this.canRequestViaIndividualPlan) return true;

      // Accès via cabinet (si membre d'un cabinet, il a accès)
      if (this.cabinetId) return true;

      return false;
    },

    // Vérifie si le cabinet admin a un abonnement expiré ou en erreur
    hasExpiredOrFailedSubscription() {
      // Plan student ou premium avec problème
      const hasIndividualPlan = ["student", "premium"].includes(this.sub.plan);

      if (!hasIndividualPlan) return false;

      // Vérifier si expiré
      let expired = false;
      if (this.sub.current_period_end) {
        const periodEnd = new Date(this.sub.current_period_end);
        const now = new Date();
        expired = periodEnd <= now;
      }

      // Vérifier si paiement échoué
      const paymentFailed = this.sub.status === 'past_due';

      return expired || paymentFailed;
    },

    quotaRemaining() {
      // For trialing plan, show days remaining instead of queries
      if (this.sub.plan === "trialing") {
        if (!this.sub.current_period_end) return "?";

        // Calculate days remaining from current_period_end
        const endDate = new Date(this.sub.current_period_end);
        const today = new Date();

        // Reset hours to get accurate day count
        today.setHours(0, 0, 0, 0);
        endDate.setHours(0, 0, 0, 0);

        // Calculate difference in days
        const diffTime = endDate - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        // Return the number of days, minimum 0
        return Math.max(0, diffDays);
      }

      // Original logic for other plans
      if (this.sub.plan === "student") return Math.max(0, 25 - this.sub.queries_used);
      if (this.sub.plan === "premium") return Math.max(0, 250 - this.sub.queries_used);
      return "∞";
    },
    progressBarWidth() {
      if (this.sub.plan === "lifetime_free") return "100%";

      // For trialing plan, calculate progress based on trial duration
      if (this.sub.plan === "trialing") {
        if (!this.sub.current_period_end || !this.sub.current_period_start) {
          return "0%";
        }

        const startDate = new Date(this.sub.current_period_start);
        const endDate = new Date(this.sub.current_period_end);
        const today = new Date();

        const totalDuration = endDate - startDate;
        const elapsed = today - startDate;

        // Calculate percentage of trial used
        const percentUsed = (elapsed / totalDuration) * 100;
        return `${Math.min(100, Math.max(0, percentUsed))}%`;
      }

      // Original logic for other plans
      let totalQuota = 25;
      if (this.sub.plan === "premium") totalQuota = 250;

      const usedPercentage = (1 - this.quotaRemaining / totalQuota) * 100;
      return `${Math.min(100, Math.max(0, usedPercentage))}%`;
    },
    userInitials() {
      if (!this.userEmail) return "U";
      return this.userEmail.substring(0, 1).toUpperCase();
    }
  },

  methods: {
    toggleSettingsMenu() {
      this.showSettingsMenu = !this.showSettingsMenu;
    },

    async handleSignout() {
      try {
        clearSessionCache(); // ✅ Clear the cache on sign out
        await logout();
        this.showSettingsMenu = false;
        this.adminMenuItems = [];
        this.$router.push("/");
      } catch (e) {
        console.error("Error signing out:", e);
      }
    },

    closeSettingsMenu(e) {
      if (this.showSettingsMenu && !e.target.closest(".settings-dropdown")) {
        this.showSettingsMenu = false;
      }
    },

    async refreshSubscription() {
      if (this.isRefreshingSubscription) {
        return;
      }

      this.isRefreshingSubscription = true;

      // Ne pas bloquer l'UI si on a déjà chargé une fois
      if (!this.hasLoadedOnce) {
        this.loadingSub = true;
      }

      try {
        const newSub = await getSubscriptionCached();
        this.sub = newSub;

        // ✅ Use the simplified session function
        const session = await getSupabaseSession();

        if (session?.user) {
          this.userEmail = session.user.email;
          this.userId = session.user.id;
        }

        this.hasLoadedOnce = true;
      } catch (e) {
        // Garder les anciennes valeurs si on en a déjà
        if (!this.hasLoadedOnce) {
          this.sub = { plan: "none", status: "none", queries_used: 0 };
        }
      } finally {
        this.loadingSub = false;
        this.isRefreshingSubscription = false;
      }
    },

    navigateToContact() {
      // Navigate to the contact page
      this.showSubscriptionModal = false;          // ferme la modal
      this.$router.push('/contactPage');
    },

    async loadAdminMenuItems() {
      if (this.isLoadingAdminMenu) {
        return;
      }

      this.isLoadingAdminMenu = true;

      try {
        const session = await getSupabaseSession();

        if (!session?.access_token) {
          this.adminMenuItems = [];
          return;
        }

        const response = await fetch(`${API_URL}/copilot/get-available-menu-items`, {
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          this.adminMenuItems = data.menu_items || [];
        } else {
          this.adminMenuItems = [];
        }

        this.menuItemsLoaded = true;
      } catch (e) {
        console.error('❌ Erreur chargement menu admin:', e);
        this.adminMenuItems = [];
        this.menuItemsLoaded = true;
      } finally {
        this.isLoadingAdminMenu = false;
      }
    },


    navigateToTrial() {
      // Navigate to the trial page
      this.showSubscriptionModal = false;          // ferme la modal
      this.$router.push('/trialPage');
    },

    navigateToStudent() {
      // Navigate to the student page
      this.showSubscriptionModal = false;          // ferme la modal
      this.$router.push('/studentPage');
    },

    upgrade() {
      // Self-checkout retiré : la conversion passe désormais par un conseiller (sales-led).
      // On redirige vers la page de contact au lieu d'ouvrir Stripe Checkout.
      this.navigateToContact();
    },

    // Méthode utilisée par le modal de souscription
    upgradeThroughModal(priceId) {
      this.showSubscriptionModal = false;
      this.showSettingsMenu = false;
      this.upgrade(priceId);
    },

    // Méthode pour gérer la demande d'essai
    requestTrial() {
      this.showSubscriptionModal = false;
      this.showSettingsMenu = false;
      // Rediriger vers la page de contact ou montrer un formulaire
      this.$router.push({ name: "contact" });
      this.$toast.info(this.$t('navbar.toasts.trialRequested'));
    },

    async openBillingPortal() {
      // If user is trialing, show subscription modal instead of billing portal
      if (this.sub.plan === "trialing") {
        this.showSubscriptionModal = true;
        this.showSettingsMenu = false; // Close the settings menu
        return;
      }

      try {
        // 1) Récupérer le JWT depuis Supabase
        const { data } = await supabase.auth.getSession();
        const jwt = data?.session?.access_token;
        const userId = data?.session?.user?.id;
        if (!jwt) throw new Error(this.$t('navbar.toasts.sessionExpired'));

        // 2) Appeler l'API Flask directement via fetch
        const response = await fetch(`${API_URL}/copilot/create-billing-portal-session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${jwt}`
          },
          body: JSON.stringify({ userId: userId })
        });

        // 3) Vérifier la réponse
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || this.$t('navbar.toasts.billingPortalError'));
        }

        const result = await response.json();
        // 4) Rediriger vers l'URL du portail de facturation
        window.location = result.url;
      } catch (err) {
        this.$toast.error(err.message);
      }
    },

    async handleWindowFocus() {

      // ✅ Debounce: Skip if we refreshed recently (within last 2 seconds)
      const now = Date.now();
      if (this._lastRefreshTime && (now - this._lastRefreshTime) < 2000) {
        return;
      }

      // Skip if already refreshing or loading
      if (this.isRefreshingSubscription || this.isLoadingAdminMenu) {
        return;
      }

      this._lastRefreshTime = now;

      try {
        // ✅ Use the simplified session function
        const session = await getSupabaseSession();

        if (!session) {
          return;
        }

        // Now run both in parallel - they will use the fresh cached session
        await Promise.all([
          this.refreshSubscription(),
          this.userId ? this.loadAdminMenuItems() : Promise.resolve()
        ]);
      } catch (e) {
        // Intentionally empty - refresh continues on error
      }
    },
  },

  async mounted() {
    document.addEventListener("click", this.closeSettingsMenu);
    window.addEventListener('popstate', this.handlePopState);
    window.addEventListener('focus', this.handleWindowFocus); // ✅ Keep this

    // ✅ Initialize debounce timestamp
    this._lastRefreshTime = Date.now();

    // Load subscription once at mount and whenever auth changes
    await this.refreshSubscription();
    try {
      const p = await getOwnProfile();
      // Vue admin cabinet = is_cab_admin ET cabinet.admin_view_enabled (découple payeur rattaché ↔ vue admin)
      this.isCabinetAdmin = (p?.is_cab_admin && p?.admin_view_enabled) || false;
      this.cabinetId      = p?.cabinet_id     || null;

    } finally {
      this.profileLoaded = true;
    }

    await this.loadAdminMenuItems();

    if (this.userId) {
      startHeartbeat();
    }

    if (!this.authSubscription) {
      const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event) => {

        // Ignorer SIGNED_IN si on a déjà un userId (c'est un refresh, pas une vraie connexion)
        if (event === 'SIGNED_IN' && this.userId) {
          return;
        }

        if (event === 'SIGNED_OUT') {
          stopHeartbeat();
          clearSessionCache();
          this.adminMenuItems = [];
          this.sub = { plan: "none", status: "none", queries_used: 0 };
          this.userEmail = "";
          this.userId = null;
          this.hasLoadedOnce = false;
        }

        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          this._lastRefreshTime = Date.now();
          await this.refreshSubscription();
          await this.loadAdminMenuItems();
          if (event === 'SIGNED_IN') startHeartbeat();
        }
      });
      this.authSubscription = subscription;
    }
  },

  beforeDestroy() {
    stopHeartbeat();
    this.authSubscription?.unsubscribe();
    this.authSubscription = null;
    document.removeEventListener("click", this.closeSettingsMenu);

    // ✅ ADD THIS: Clean up focus listener
    window.removeEventListener('focus', this.handleWindowFocus);
  },
};
</script>

<style scoped>
/* Global resets and font-family rules */
body {
  margin: 0;
  font-family: 'Roboto', sans-serif;
}

#app {
  margin: 0;
  padding: 0;
  height: 100%;
}

/* Navigation */
.my-nav {
  display: flex;
  align-items: center;
  background-color: #120D31;
  padding: 0.5rem 1rem;
}

.my-nav-brand {
  margin-right: 125px;
  text-decoration: none;
}

.my-nav-logo {
  height: 50px;
}

/* Link list */
.my-nav-links {
  display: flex;
  flex: 1;             /* allows the ul to take remaining space */
  list-style-type: none;
  padding: 0;
  margin: 0;
}

.my-nav-links li {
  margin-right: 1.5rem;
  font-weight: 400;
}

/* Base style for nav links */
.my-nav-link {
  position: relative;
  color: #FFFFFF;
  text-decoration: none;
  font-weight: 400;
  font-size: 0.8em;
  padding: 0.5rem 0;
  transition: color 0.3s ease, transform 0.3s ease;
}

/* Underline effect */
.my-nav-link::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: 0;
  height: 2px;
  width: 0;
  background-color: #FFFFFF;
  transition: width 0.3s ease;
}

/* Hover state */
.my-nav-link:hover {
  transform: translateY(-3px);
  color: #e0e0e0;
}

.my-nav-link:hover::after {
  width: 100%;
}

/* Disabled state remains unchanged */
.my-nav-link.disabled {
  opacity: 0.5;
  pointer-events: none;
}

.my-nav-settings {
  margin-left: auto;
}

.my-nav-icon {
  height: 45px;
  width: 25px;
}

/* Settings dropdown */
.settings-dropdown {
  position: relative;
}

/* Enhanced settings menu styles */
.settings-menu {
  position: absolute;
  top: 100%;
  right: 0;
  width: 280px;
  background-color: #FFFFFF;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  margin-top: 15px;
  z-index: 1000;
  overflow: hidden;
}

/* User profile section */
.user-profile {
  display: flex;
  align-items: center;
  padding: 16px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #eaeaea;
}

.user-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: #6c63ff;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  margin-right: 12px;
}

.user-info {
  flex: 1;
  overflow: hidden;
}

.user-email {
  margin: 0;
  font-size: 0.9rem;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Subscription section */
.subscription-box {
  padding: 16px;
}

.subscription-status {
  margin-bottom: 16px;
}

.plan-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.plan-title {
  margin: 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: #333;
}

.plan-badge {
  padding: 4px 8px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
}

.plan-none {
  background-color: #f5f5f5;
  color: #999;
}

.plan-student {
  background-color: #e8f5e9;
  color: #2e7d32;
}

.plan-premium {
  background-color: #e3f2fd;
  color: #1565c0;
}

.plan-lifetime_free {
  background-color: #fff3e0;
  color: #e65100;
}

/* Usage meter */
.usage-meter {
  margin-bottom: 16px;
}

.usage-label {
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
  color: #666;
  margin-bottom: 6px;
}

.usage-count {
  font-weight: 600;
  color: #333;
}

.progress-bar {
  height: 6px;
  background-color: #f0f0f0;
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background-color: #6c63ff;
  border-radius: 3px;
  transition: width 0.3s ease;
}

/* Unlimited plan badge */
.unlimited-plan {
  margin: 12px 0;
  text-align: center;
}

.unlimited-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  background-color: #f0f0f0;
  border-radius: 20px;
  font-size: 0.8rem;
  color: #333;
}

.unlimited-badge svg {
  margin-right: 6px;
}

/* Payment alert */
.payment-alert {
  padding: 8px 12px;
  background-color: #ffebee;
  color: #c62828;
  border-radius: 6px;
  font-size: 0.8rem;
  margin: 10px 0;
}

/* Button styles */
.plan-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* Upgrade button - new style for the single upgrade button */
.upgrade-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  background-color: #6c63ff;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
}

.upgrade-btn:hover {
  background-color: #5a54d1;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(108, 99, 255, 0.2);
}

.upgrade-btn svg {
  transition: transform 0.3s ease;
}

.upgrade-btn:hover svg {
  transform: rotate(90deg);
}

.billing-btn {
  padding: 10px 16px;
  background-color: #f5f5f5;
  color: #333;
  border: none;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.2s ease;
  width: 100%;
}

.billing-btn:hover {
  background-color: #e8e8e8;
  transform: translateY(-2px);
}

/* Loading animation */
.loading-subscription {
  padding: 20px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 10px;
}

.loading-spinner {
  width: 24px;
  height: 24px;
  border: 3px solid rgba(108, 99, 255, 0.2);
  border-radius: 50%;
  border-top: 3px solid #6c63ff;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Account actions section */
.account-actions {
  padding: 12px 16px;
  background-color: #f8f9fa;
  border-top: 1px solid #eaeaea;
}

.signout-button {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 16px;
  border: none;
  background-color: transparent;
  color: #e53935;
  text-align: left;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  border-radius: 8px;
  transition: background-color 0.2s ease;
}

.signout-button:hover {
  background-color: #ffebee;
}

.change-password-link {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 16px;
  color: #333;
  text-decoration: none;
  font-size: 0.85rem;
  font-weight: 500;
  border-radius: 8px;
  transition: background-color 0.2s ease;
  margin-bottom: 8px;
}

.change-password-link:hover {
  background-color: #e8e8e8;
}

/* Dropdown animation */
.fade-down-enter-active, .fade-down-leave-active {
  transition: all 0.3s ease;
}

.fade-down-enter, .fade-down-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

/* Content area */
.content {
  height: calc(100vh - 70px); /* Adjust based on your navbar height */
  overflow-y: auto;
}
</style>
